/**
 * Anonymous "shadow" progress kept in localStorage while logged out. On register
 * it is POSTed to `/api/public/guest-progress/import` (the server RE-CHECKS access
 * per item — the blob is never trusted) and then cleared. Keyed by moduleId,
 * keep-best on score so a replay never lowers a stored value. Bounded + TTL'd so
 * it can never grow unbounded or resurrect ancient state.
 */
const KEY = 'acp-guest-progress'
const MAX_ITEMS = 500
const TTL_MS = 30 * 24 * 60 * 60 * 1000 // 30 days

export interface GuestModuleProgress {
  moduleId: string
  score?: number
  timeMs?: number
  hintsUsed?: number
}

export interface GuestProgressPayload {
  modules: GuestModuleProgress[]
  skips: Array<{ lessonId: string }>
}

interface GuestProgressBlob extends GuestProgressPayload {
  v: 1
  updatedAt: number
}

function empty(): GuestProgressBlob {
  return { v: 1, updatedAt: Date.now(), modules: [], skips: [] }
}

function read(): GuestProgressBlob {
  try {
    const raw = localStorage.getItem(KEY)
    if (!raw) return empty()
    const blob = JSON.parse(raw) as Partial<GuestProgressBlob>
    if (!blob || blob.v !== 1 || !Array.isArray(blob.modules)) return empty()
    if (typeof blob.updatedAt === 'number' && Date.now() - blob.updatedAt > TTL_MS) return empty()
    return {
      v: 1,
      updatedAt: blob.updatedAt ?? Date.now(),
      modules: blob.modules,
      skips: Array.isArray(blob.skips) ? blob.skips : [],
    }
  } catch {
    return empty()
  }
}

function write(blob: GuestProgressBlob): void {
  try {
    localStorage.setItem(KEY, JSON.stringify({ ...blob, updatedAt: Date.now() }))
  } catch {
    /* quota exceeded / SSR / disabled storage — fail open, never block play */
  }
}

/** Upsert a module completion (keep-best on score/time/hints). Bounded. */
export function addGuestModuleProgress(item: GuestModuleProgress): void {
  const blob = read()
  const existing = blob.modules.find((m) => m.moduleId === item.moduleId)
  if (existing) {
    existing.score = Math.max(existing.score ?? 0, item.score ?? 0)
    existing.timeMs = Math.max(existing.timeMs ?? 0, item.timeMs ?? 0)
    existing.hintsUsed = Math.max(existing.hintsUsed ?? 0, item.hintsUsed ?? 0)
  } else {
    if (blob.modules.length >= MAX_ITEMS) return
    blob.modules.push(item)
  }
  write(blob)
}

/** Record an anonymous lesson skip (deduped). Bounded. */
export function addGuestLessonSkip(lessonId: string): void {
  const blob = read()
  if (blob.skips.some((s) => s.lessonId === lessonId)) return
  if (blob.skips.length >= MAX_ITEMS) return
  blob.skips.push({ lessonId })
  write(blob)
}

/** The import payload, or null when there is nothing to migrate. */
export function readGuestProgress(): GuestProgressPayload | null {
  const blob = read()
  if (!blob.modules.length && !blob.skips.length) return null
  return { modules: blob.modules, skips: blob.skips }
}

export function clearGuestProgress(): void {
  try {
    localStorage.removeItem(KEY)
  } catch {
    /* ignore */
  }
}

export function hasGuestProgress(): boolean {
  return readGuestProgress() !== null
}
